import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { ArticleCard } from '../components/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';

import { API, authFetch } from '../lib/api';

const TOPIC_DESCRIPTIONS = {
  technology: 'Explore the latest in tech innovation, gadgets, and digital transformation.',
  ai: 'Artificial Intelligence, Machine Learning, and the future of intelligent systems.',
  programming: 'Code, software development, and engineering best practices.',
  business: 'Entrepreneurship, strategy, and building successful ventures.',
  'personal development': 'Growth mindset, productivity, and becoming your best self.',
  design: 'UI/UX, visual design, and creating beautiful experiences.',
  science: 'Scientific discoveries, research, and understanding our world.',
  health: 'Wellness, fitness, and maintaining a healthy lifestyle.',
  productivity: 'Time management, tools, and working smarter.',
  startups: 'Building companies from the ground up.'
};

export const TopicPage = () => {
  const { topicName } = useParams();
  const { user } = useAuth();
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const normalizedTopic = topicName?.toLowerCase() || '';
  const description = TOPIC_DESCRIPTIONS[normalizedTopic] || `Articles about ${topicName}`;

  useEffect(() => {
    fetchArticles();
  }, [topicName]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/tags/${normalizedTopic}?limit=50`);
      
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    }
    setLoading(false);
  };

  const handleBookmarkChange = (articleId, isBookmarked) => {
    setArticles(prev => prev.map(article => 
      article.article_id === articleId 
        ? { ...article, is_bookmarked: isBookmarked }
        : article
    ));
  };

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="topic-page">
      <Helmet>
        <title>{`${topicName} articles | The Blogs Grid`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`https://theblogsgrid.com/topic/${encodeURIComponent(topicName || '')}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${topicName} on The Blogs Grid`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`https://theblogsgrid.com/topic/${encodeURIComponent(topicName || '')}`} />
        <meta property="og:image" content="https://theblogsgrid.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${topicName} on The Blogs Grid`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://theblogsgrid.com/og-image.png" />
      </Helmet>
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Topic Header */}
        <div className="mb-10 fade-up">
          <h1 
            className="text-4xl sm:text-5xl font-bold mb-4 capitalize"
            style={{ fontFamily: 'Fraunces, serif' }}
            data-testid="topic-title"
          >
            {topicName}
          </h1>
          <p className="text-lg text-muted-foreground">
            {description}
          </p>
        </div>

        {/* Articles */}
        <div className="divide-y divide-border stagger-children">
          {loading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="py-6">
                <div className="flex gap-6">
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-3" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4 mb-3" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                  <Skeleton className="hidden sm:block w-32 h-32 rounded-lg" />
                </div>
              </div>
            ))
          ) : articles.length > 0 ? (
            articles.map(article => (
              <ArticleCard 
                key={article.article_id} 
                article={article}
                onBookmarkChange={handleBookmarkChange}
              />
            ))
          ) : (
            <div className="py-16 text-center">
              <p className="text-muted-foreground">
                No articles found for this topic yet.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TopicPage;
